/**
 * Decides whether an incoming Chatwoot subscription message is the bot's own
 * answer echoing back — already rendered in the widget from the SSE `delta`
 * stream — so the LP chat panel must NOT render it a second time.
 *
 * glue writes every bot answer into Chatwoot for the agent transcript; that
 * `message.created` returns to the widget over the subscription. `sender.type`
 * can't distinguish it from a real human reply (the bot posts with a SuperAdmin
 * *user* token → `sender.type: "user"`), so we identify the echo two ways:
 *
 *  1. **id** — glue sends the Chatwoot message id of each answer it posts via a
 *     `chatwoot_echo` SSE event (and the echo endpoint returns the id of each
 *     launcher line it posts); the panel collects those into `suppressedIds`.
 *     Precise and unambiguous.
 *  2. **queued text** — the exact string of a line the widget just asked glue to
 *     echo, reserved synchronously in `suppressedTexts` before the request goes
 *     out. This closes the race where Chatwoot broadcasts the outgoing message
 *     over the subscription before the echo endpoint returns its id. It matters
 *     for the echoed **system** turns (answer_failed, connecting_team, …): the
 *     bot-turn content match below is role-scoped and would not catch them, so
 *     without this they could render as a duplicate human reply.
 *  3. **content** — a trim-tolerant match against a bot turn already on screen.
 *     A backstop for the same race on the bot answer. (The stored Chatwoot
 *     content is trimmed; the streamed turn may carry trailing whitespace —
 *     hence the trim.)
 *
 * A genuine human reply matches none: glue only ever registers *bot* message
 * ids, `suppressedTexts` holds only strings the widget itself echoed, and a human
 * effectively never types a prior bot line verbatim.
 */
export function isBotEcho(
  message: { messageId: number; content: string },
  context: {
    suppressedIds: ReadonlySet<number>
    suppressedTexts?: ReadonlySet<string>
    turns: ReadonlyArray<{ role: string; content: string }>
  }
): boolean {
  if (context.suppressedIds.has(message.messageId)) return true
  const incoming = message.content.trim()
  if (context.suppressedTexts?.has(incoming)) return true
  return context.turns.some(
    (turn) => turn.role === "bot" && turn.content.trim() === incoming
  )
}
